<template>
  <sub-section
    title="Other cash"
    helpTitle="Other cash"
    :helpText="HelpText.otherCash()"
  >
    <div
      v-for="otherCash in getOtherCash"
      v-bind:key="otherCash.id"
      class="row mb-2"
    >
      <div class="col-9 d-flex align-items-center">
        {{ otherCash.description }}
      </div>
      <div class="col-3">
        <three-dot-menu
          :id="otherCash.id"
          :permissions="getPermissionSet(otherCash.editMode, false, true, true)"
          :aria-purpose="otherCash.name"
          @editClicked="onEdit"
          @deleteClicked="onDelete"
        />
      </div>
    </div>

    <div v-show="!hasOtherCash">
        <empty-asset name="cash" />
    </div>

    <div class="row mt-4">
      <add-asset name="cash" longName="any other cash" @addClicked="onAdd()" ref="addAssetComponent"  />
    </div>

    <div v-if="showEquityReleaseDashboardPrompt" class="row mt-3">
      <div class="col d-flex align-items-center">
        <p class="text-muted align-middle mb-0">
          If you wish to consider the Equity Release route further, check out the Equity Release page{{ equityReleasePromptLocationPostfix }}.
        </p>
      </div>
    </div>
  </sub-section>

  <modal :title="modalTitle" ref="assetModal" idKey="otherCash" close-button-aria-label="Close other cash popup editor" :returnFocusTo="addAssetButton">
    <other-cash-editor ref="assetEditor" @edit-finished="onEditFinished" />
  </modal>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import SubSection from "@/guiide/components/common/SubSection.vue";
import HelpText from "@/guiide/ts/helpText";
import ThreeDotMenu from "@/guiide/components/common/ThreeDotMenu.vue";
import AddAsset from "@/guiide/components/assets/AddAsset.vue";
import EmptyAsset from "@/guiide/components/assets/EmptyAsset.vue";
import Modal from "@/guiide/components/common/Modal.vue";
import OtherCashEditor from "@/guiide/components/assets/OtherCashEditor.vue";
import ObjectBuilder from "@/guiide/ts/objectBuilder";
import Constants from "@/guiide/ts/constants";
import { app } from "@/guiide/ts/app";
import { OtherCash } from "@/guiide/ts/otherCash";
import TemporalDetail from "@/guiide/ts/temporalDetail";
import { getPermissionSet } from "@/guiide/ts/utilities";
import { useStore } from "vuex";
import CashType from "@/guiide/ts/cashType";
import View from "@/guiide/ts/view";

const store = useStore();

const assetModal = ref(null);
const assetEditor = ref(null);
const modalTitle = ref("Edit other cash");

const addAssetButton = ref<HTMLElement>(null);
const addAssetComponent = ref<InstanceType<typeof AddAsset> | null>(null);

const getOtherCash = computed<Array<OtherCash>>(() => {
  return store.state.retiree?.otherCash ?? new Array<OtherCash>();
});

const hasOtherCash = computed<boolean>(() => getOtherCash.value.length > 0);

function onAdd() {
  modalTitle.value = "Add other cash";
  const defaultReceiptDate = TemporalDetail.getOtherCashDefaultReceiptDate();
  const incrementMonthIndexToAlignWithIncorrectIndexingInMonthYearComponent = defaultReceiptDate.getMonth() + 1;
  const otherCashAsset = ObjectBuilder.createOtherCash(
    Constants.NewId,
    "",
    0,
    incrementMonthIndexToAlignWithIncorrectIndexingInMonthYearComponent,
    defaultReceiptDate.getFullYear()
  );
  assetEditor.value.setValues(otherCashAsset);

  addAssetButton.value = addAssetComponent.value.getAddButton();

  assetModal.value.showModal();
}

function onEdit(id: number) {
  modalTitle.value = "Edit other cash";
  const otherCash = getOtherCash.value.filter(p => p.id == id)[0];
  assetEditor.value.setValues(otherCash);
  assetModal.value.showModal();
}

async function onDelete(id: number) {
  await app.deleteOtherCash(id);
}

function onEditFinished() {
  assetModal.value.closeModal();
}

const showEquityReleaseDashboardPrompt = computed<boolean>(() => getOtherCash.value.some(c => c.cashType == CashType.EquityRelease));

const equityReleasePromptLocationPostfix = computed<string>(() => {
  return store.state.view == View.Haves ? " in the Dashboard" : "";
});
</script>
